import { useEffect, useState, type ReactNode } from 'react';
import { localHourFraction } from '../utils/dateUtils.js';

export interface NowIndicatorProps {
  /** Earliest hour rendered on the grid (0–23). */
  readonly startHour: number;
  /** Latest hour rendered on the grid, inclusive (0–23). */
  readonly endHour: number;
  /** Px height of one hour row. */
  readonly hourHeight: number;
  /** When false, the indicator is hidden (e.g. on non-today columns). */
  readonly active?: boolean | undefined;
  /** Show the textual `now · 11:30am` label next to the dot. Default true. */
  readonly showLabel?: boolean | undefined;
  /** Locale used for the time label. */
  readonly locale?: string | undefined;
  /** IANA TZ used to project "now" onto the grid AND to format the
   *  label. When omitted, system TZ is used (matches the default
   *  `viewerTz` resolved by CalendarView). */
  readonly viewerTz?: string | undefined;
  /** Override "now" — useful in tests. */
  readonly now?: Date | undefined;
  /** Refresh cadence in ms. Default 60_000 (one minute). */
  readonly tickMs?: number | undefined;
}

/**
 * A horizontal accent line at the current time (in `viewerTz`). The
 * minute-tick interval keeps a tab open across an hour boundary correct.
 *
 * TZ correctness: a PT-system viewer looking at an ET calendar sees
 * the line at the row of *ET* now, not PT now — the grid's events are
 * already in ET wall-clock, and this indicator must match.
 */
export function NowIndicator(props: NowIndicatorProps): ReactNode {
  const {
    startHour,
    endHour,
    hourHeight,
    active = true,
    showLabel = true,
    locale,
    viewerTz,
    now: overrideNow,
    tickMs = 60_000,
  } = props;

  const [now, setNow] = useState<Date>(() => overrideNow ?? new Date());

  useEffect(() => {
    // Mirror an externally-provided `now` so test scenarios that step
    // time forward see the indicator move.
    if (overrideNow !== undefined) {
      setNow(overrideNow);
      return undefined;
    }
    if (!active) return undefined;
    const id = window.setInterval(() => { setNow(new Date()); }, tickMs);
    return () => { window.clearInterval(id); };
  }, [tickMs, active, overrideNow]);

  if (!active) return null;
  // Project `now` into viewerTz. `localHourFraction` handles the TZ
  // math; the `?? system local` fallback only kicks in if a malformed
  // ISO ever comes through `toISOString()` — which it can't.
  const fractionTz = localHourFraction(now.toISOString(), viewerTz);
  const fraction = fractionTz ?? now.getHours() + now.getMinutes() / 60;
  if (fraction < startHour || fraction > endHour + 1) return null;
  const top = (fraction - startHour) * hourHeight;
  if (top < 0) return null;

  const label = showLabel
    ? now.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: '2-digit',
        ...(viewerTz !== undefined ? { timeZone: viewerTz } : {}),
      })
    : null;

  return (
    <div
      className="vq-cal__now-indicator"
      style={{ top }}
      aria-label={`Current time ${label ?? ''}`.trim()}
      role="presentation"
    >
      <span className="vq-cal__now-dot" aria-hidden="true" />
      {label !== null && <span className="vq-cal__now-label">{`now · ${label}`}</span>}
    </div>
  );
}
